/**
 * lib/auth/recovery-telemetry.ts
 *
 * What it does:
 *   Centralizes telemetry for the Clerk auth-recovery interceptor in
 *   lib/auth/interceptor.ts. Fires Segment events on successful recovery,
 *   both Segment + Sentry events on retry-budget exhaustion, and a
 *   Sentry-only warning when a refresh times out (not a confirmed signout).
 *
 * What it does NOT do:
 *   Has no opinion on WHEN to fire. The interceptor decides. This file
 *   only standardizes the property shape, normalizes URL cardinality,
 *   and isolates the Sentry fingerprints so failures and timeouts each
 *   collapse to a single Sentry issue.
 *
 * Depends on:
 *   - @sentry/nextjs (error-level captureMessage on failure — pages on-call;
 *     warning-level on timeout — non-paging)
 *   - @/lib/segment/segment-client (clientSideTrack)
 *   - @/lib/segment/segment-events (typed SEGMENT_EVENTS + schemas)
 */

import * as Sentry from "@sentry/nextjs";

import { clientSideTrack } from "@/lib/segment/segment-client";
import { SEGMENT_EVENTS } from "@/lib/segment/segment-events";

// matches a uuid-v4-ish segment so paths like
// /api/sites/9214d40c-6d4e-4f7c-bb83-934ef7546a08/website
// collapse to /api/sites/:id/website for low-cardinality aggregation.
const UUID_SEGMENT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// strips the querystring entirely — businessId, etc. leak into it and
// are not useful for endpoint-level grouping in segment destinations.
export function stripIds(rawUrl: string | undefined | null): string {
  if (!rawUrl) return "(unknown)";
  const withoutQuery = rawUrl.split("?")[0];
  return withoutQuery.replace(UUID_SEGMENT, ":id");
}

// best-effort read of the Clerk session state without taking a hard
// dependency on the SDK from this helper. window.Clerk is populated by
// clerk-js once it loads; before that it is undefined (see types/clerk-window.d.ts).
function readClerkSession(): { hadActiveClerkSession: boolean; clerkSessionId: string | null } {
  if (typeof window === "undefined") {
    return { hadActiveClerkSession: false, clerkSessionId: null };
  }
  const session = window.Clerk?.session ?? null;
  return {
    hadActiveClerkSession: !!session,
    clerkSessionId: session?.id ?? null,
  };
}

export function onAuthRecoverySucceeded(args: { url: string | undefined; msToRecovery: number }) {
  clientSideTrack({
    event: SEGMENT_EVENTS.AUTH_RECOVERY_SUCCEEDED,
    properties: {
      swrKey: stripIds(args.url),
      msToRecovery: args.msToRecovery,
    },
  });
}

export function onAuthRecoveryFailed(args: {
  url: string | undefined;
  statusCode: 401;
  msToRecovery: number;
  cause?: unknown;
}) {
  const swrKey = stripIds(args.url);
  const { hadActiveClerkSession, clerkSessionId } = readClerkSession();

  clientSideTrack({
    event: SEGMENT_EVENTS.AUTH_RECOVERY_FAILED,
    properties: {
      swrKey,
      statusCode: args.statusCode,
      msToRecovery: args.msToRecovery,
      hadActiveClerkSession,
      clerkSessionId,
    },
  });

  // single fingerprint collapses every failure into one Sentry issue.
  // sentry-side alerting can then watch the event count of that one issue
  // instead of drowning in distinct issues per swrKey.
  //
  // level "error" so:
  //   1. session replay attaches (replaysOnErrorSampleRate gates on error+)
  //   2. the V6 page-on-call alert threshold (>10/hr) fires
  // the fingerprint collapses all events into one issue regardless of level,
  // so this does NOT increase Sentry noise — it replaces the dozens of
  // distinct AxiosError 401 / "No orgId in auth object" / "Authentication
  // required" error-level events with ONE error-level event per failed
  // recovery, all under the same auth-recovery-failed group.
  Sentry.captureMessage("Auth recovery failed after retries", {
    level: "error",
    fingerprint: ["auth-recovery-failed"],
    tags: {
      auth_recovery: "failed",
      swr_key: swrKey,
    },
    extra: {
      msToRecovery: args.msToRecovery,
      hadActiveClerkSession,
      clerkSessionId,
      cause: args.cause,
    },
  });
}

export function onAuthRecoveryTimedOut(args: { msWaited: number; consecutiveTimeouts: number }) {
  // A refresh that blows the timeout budget is "couldn't confirm in time",
  // not "confirmed signed out" — slow mobile networks and a congested FAPI
  // do this legitimately. Warning level + its own fingerprint so these
  // collapse into a separate non-paging issue: they must never trip the
  // error-level auth-recovery-failed on-call alert.
  Sentry.captureMessage("Auth recovery refresh timed out", {
    level: "warning",
    fingerprint: ["auth-recovery-timeout"],
    tags: {
      auth_recovery: "timeout",
    },
    extra: {
      msWaited: args.msWaited,
      consecutiveTimeouts: args.consecutiveTimeouts,
    },
  });
}
